/**
 * StatusBadge -- Displays a colored status indicator with optional dot.
 * @layer atom
 * @package @plerity/ui-core
 */

import { cva } from "class-variance-authority";
import { memo } from "react";
import { cn } from "../utils/cn";
import type { StatusBadgeProps, StatusLevel, StatusVariant } from "./StatusBadge.types";

/**
 * Color classes per severity — the canonical color SSoT for status
 * treatments fleet-wide. Also consumed by `@plerity/ui-core/status-styles`
 * (e.g. FreshnessBadge's `fresh`/`old` reuse `success`/`error` directly)
 * so a new severity color only needs to change here.
 */
export const STATUS_COLOR_CLASSES: Record<StatusVariant, string> = {
  success: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  // high: a distinct orange hue between warning (yellow) and error (red), for
  // 4-tier severity domains (low/medium/high/critical) that need `high` to
  // read differently from both. Contrast pairs asserted in
  // StatusBadge.contrast.test.ts (orange-100/orange-800 light 6.4:1;
  // orange-400 on gray-900 dark 7.8:1 — both WCAG AA). `orange-*` is a real
  // Tailwind palette family (not phantom), already rendered in the SPA build.
  high: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  error: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  danger: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  info: "bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400",
  // neutral: explicit dark: pair (matches the 18 sibling ui-core atoms swept
  // in the 2026-06-18 Option-B fleet fix — StatusBadge's neutral was missed).
  // The old bare-gray "auto-inverts against tokens-apps' INVERTING ramp"
  // assumption is stale: that ramp was stripped by the Tailwind v4 build
  // (2026-06-18) and replaced by a STATIC runtime-injected ramp (2026-07-07,
  // token-inject-runtime), so bare gray-100/gray-800 stays LIGHT in web dark
  // mode. gray-700/gray-300 keep literal fallbacks and survive the strip.
  neutral: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
};

const badgeVariants = cva("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium", {
  variants: {
    status: STATUS_COLOR_CLASSES,
  },
  defaultVariants: {
    status: "neutral",
  },
});

const dotColors: Record<StatusVariant, string> = {
  success: "bg-green-500",
  warning: "bg-yellow-500",
  high: "bg-orange-500",
  error: "bg-red-500",
  danger: "bg-red-500",
  info: "bg-brand-500",
  neutral: "bg-gray-500",
};

/** Map any string to a known StatusVariant, falling back to `neutral`. */
export function resolveStatusVariant(variant: string | null | undefined): StatusVariant {
  return variant != null && Object.hasOwn(STATUS_COLOR_CLASSES, variant) ? (variant as StatusVariant) : "neutral";
}

export const StatusBadge = memo(function StatusBadge({
  label,
  status = "neutral",
  showDot,
  statusMap,
  pulse,
  dotClassName,
  className,
}: StatusBadgeProps) {
  let effectiveVariant: StatusVariant = (status as StatusVariant) ?? "neutral";
  let displayLabel = label ?? status;
  // A matching statusMap entry's own `pulse` wins over the component-level prop.
  let effectivePulse = pulse ?? false;

  if (statusMap) {
    const config = statusMap[status] ?? { variant: "neutral" as StatusVariant, label: status };
    effectiveVariant = config.variant;
    displayLabel = config.label;
    effectivePulse = config.pulse ?? pulse ?? false;
  }

  // Unknown variant (e.g. a raw lifecycle string like "completed" passed as
  // `status` without a statusMap) previously matched no cva key and no
  // dotColors entry → the badge rendered with NO colour and an invisible dot.
  // Fall back to neutral so it is always legible (2026-10-11 AI visual QA).
  effectiveVariant = resolveStatusVariant(effectiveVariant);

  const showIndicator = showDot ?? true;

  return (
    // Use <span role="status"> instead of <output>: <output> is semantically for
    // form-computed results; role="status" on a span is the correct ARIA pattern
    // for a live status indicator (WCAG 4.1.2).
    // biome-ignore lint/a11y/useSemanticElements: see comment above — <output> is the wrong semantic element for a status badge, so role="status" on <span> is intentional, not an oversight.
    <span role="status" className={cn(badgeVariants({ status: effectiveVariant as StatusLevel }), className)}>
      {showIndicator && (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            dotColors[effectiveVariant],
            effectivePulse && "animate-pulse",
            dotClassName
          )}
          aria-hidden="true"
        />
      )}
      {displayLabel}
    </span>
  );
});

StatusBadge.displayName = "StatusBadge";
